import React, { useEffect, useMemo, useCallback, useReducer } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Cookies from 'js-cookie';

import {
  Col, Row, UncontrolledTooltip, Modal, ModalHeader, ModalBody, Form,
  Input, FormFeedback, Label, Card, CardBody, Button, Pagination,
  PaginationItem, PaginationLink, ModalFooter, Badge
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TableContainer from '../../components/Common/TableContainer';
import Spinners from "components/Common/Spinner";
import Breadcrumbs from '../../components/Common/Breadcrumb';
import DeleteModal from '../../components/Common/DeleteModal';
import {
  fetchBacaan,
  createBacaan,
  updateBacaan,
  deleteBacaan,
  handleViewFile,
} from '../../services/bacaanService';
import { fetchJenisSoal } from '../../services/Master/MasterJenisSoal';
import { fetchJenisSeleksi } from '../../services/Master/MasterJenisSeleksi';
import { fetchTipeKonten } from '../../services/Master/MasterTipeKonten';
import { fetchKategori } from '../../services/Master/kategoriService.js';

const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 10;
const DEFAULT_FILTERS = {
  id_jenis_soal: "",
  id_jenis_seleksi: "",
  id_tipe_konten: "",
  id_kategori_paket: "",
  search: "",
  id_bacaan: ""
};

const initialState = {
  modal: false,
  isEdit: false,
  bacaan: null,
  data: [],
  loading: true,
  loadingOptions: false,
  loadingJenisSoalModal: false,
  jenisSoalOptions: [],
  jenisSoalModalOptions: [],
  jenisSeleksiOptions: [], // This is not currently used directly, but keep for completeness if needed elsewhere
  tipeKontenOptions: [],
  kategoriOptions: [],
  filteredJenisSeleksi: [],
  filteredJenisSeleksiModal: [],
  isOptionsLoaded: false,
  deleteModal: false,
  pagination: {
    page: DEFAULT_PAGE,
    per_page: DEFAULT_PER_PAGE,
    total: 0,
    last_page: 1
  },
  filters: { ...DEFAULT_FILTERS }
};
const getAuthToken = () => Cookies.get('access_token');


function reducer(state, action) {
  switch (action.type) {
    case 'SET_STATE':
      return {
        ...state,
        ...action.payload,
        // Ensure kategoriOptions is not overwritten if not provided in payload
        kategoriOptions: action.payload.kategoriOptions !== undefined
          ? action.payload.kategoriOptions
          : state.kategoriOptions
      };
    case 'SET_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
        pagination: { ...state.pagination, page: DEFAULT_PAGE }
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: { ...state.pagination, ...action.payload }
      };
    case 'RESET_FILTERS':
      return {
        ...state,
        filters: { ...DEFAULT_FILTERS },
        pagination: { ...state.pagination, page: DEFAULT_PAGE }
      };
    case 'FILTER_JENIS_SELEKSI':
      return {
        ...state,
        filteredJenisSeleksi: action.payload
      };
    case 'FILTER_JENIS_SELEKSI_MODAL':
      return {
        ...state,
        filteredJenisSeleksiModal: action.payload
      };
    case 'SET_JENIS_SOAL_MODAL_OPTIONS':
      return {
        ...state,
        jenisSoalModalOptions: action.payload
      };
    default:
      return state;
  }
}

const BacaanList = () => {
  document.title = "Daftar Bacaan";
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idBacaan = queryParams.get('id_bacaan');

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    filters: { ...initialState.filters, id_bacaan: idBacaan || "" }
  });

  const {
    modal, isEdit, bacaan, data, loading,
    jenisSoalOptions, tipeKontenOptions, kategoriOptions,
    filteredJenisSeleksi, filteredJenisSeleksiModal,
    jenisSoalModalOptions, loadingJenisSoalModal,
    isOptionsLoaded, deleteModal, pagination, filters, loadingOptions
  } = state;

  // Mendefinisikan handleSubmit di awal
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitting(true);
      const formData = new FormData();

      Object.keys(values).forEach(key => {
        if (key === 'thumbnail_image' || key === 'file_bacaan') {
          if (values[key] instanceof File) {
            formData.append(key, values[key]);
          }
        } else if (key !== 'remove_thumbnail' && key !== 'isEdit') { // Jangan append remove_thumbnail dan isEdit jika bukan file
          formData.append(key, values[key] !== null ? values[key] : '');
        }
      });

      // Hanya tambahkan remove_thumbnail jika benar dan sedang dalam mode edit
      if (values.remove_thumbnail && isEdit) {
        formData.append('remove_thumbnail', '1');
      }

      const response = isEdit
        ? await updateBacaan(bacaan.id_bacaan, formData)
        : await createBacaan(formData);

      if (response.success) {
        toast.success(response.message || (isEdit ? "Bacaan berhasil diperbarui" : "Bacaan berhasil ditambahkan"));
        fetchData(); // Muat ulang data setelah sukses
        toggleModal(); // Tutup modal
        if (idBacaan && !isEdit) {
          navigate(location.pathname, { replace: true });
        }
      } else {
        throw new Error(response.message || "Gagal menyimpan bacaan.");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error(error.message || "Terjadi kesalahan saat menyimpan.");
    } finally {
      setSubmitting(false);
    }
  };

  // PENTING: ValidationSchema harus didefinisikan sebelum formik
  const validationSchema = Yup.object().shape({
    id_tipe_konten: Yup.string().required('Tipe Konten wajib diisi'),
    id_jenis_seleksi: Yup.string().required('Jenis Seleksi wajib diisi'),
    id_jenis_soal: Yup.string().required('Jenis Soal wajib diisi'),
    id_kategori_paket: Yup.string().required('Kategori wajib diisi'),
    judul: Yup.string().required('Judul wajib diisi'),
    deskripsi: Yup.string().required('Deskripsi wajib diisi'),
    thumbnail_image: Yup.mixed()
      .test('fileSize', 'Ukuran gambar terlalu besar (maksimal 2MB)', (value) => {
        if (typeof value === 'string' || (value === null && formik.values.isEdit)) return true;
        if (value instanceof File) return value.size <= 2 * 1024 * 1024;
        return true;
      })
      .test('fileType', 'Format gambar tidak didukung (hanya: jpg, jpeg, png)', (value) => {
        if (typeof value === 'string' || (value === null && formik.values.isEdit)) return true;
        if (value instanceof File) return ['image/jpeg', 'image/png', 'image/jpg'].includes(value.type);
        return true;
      })
      .test('is-required', 'Thumbnail wajib diisi', function(value) {
        const isCurrentlyEditing = this.parent.isEdit;
        const hasExistingThumbnail = bacaan?.thumbnail_image;
        const isRemovingThumbnail = this.parent.remove_thumbnail;

        // Jika dalam mode edit, ada thumbnail lama, DAN TIDAK dihapus
        if (isCurrentlyEditing && hasExistingThumbnail && !isRemovingThumbnail) {
          return true; // Validasi terpenuhi, tidak perlu file baru
        }

        // Jika ada file baru yang diunggah
        if (value instanceof File) {
          return true;
        }

        // Jika bukan mode edit (add baru) ATAU mode edit tapi tidak ada thumbnail lama ATAU thumbnail lama dihapus, maka file baru wajib ada
        return !!value; // Cek apakah 'value' (file input baru) ada
      }),
    file_bacaan: Yup.mixed()
      .test('fileSize', 'Ukuran file terlalu besar (maksimal 5MB)', (value) => {
        if (typeof value === 'string' || (value === null && formik.values.isEdit)) return true;
        if (value instanceof File) return value.size <= 5 * 1024 * 1024;
        return true;
      })
      .test('fileType', 'Hanya file PDF yang diperbolehkan', (value) => {
        if (typeof value === 'string' || (value === null && formik.values.isEdit)) return true;
        if (value instanceof File) return value.type === 'application/pdf';
        return true;
      })
      .test('is-required', 'File bacaan wajib diisi', function(value) {
        const isCurrentlyEditing = this.parent.isEdit;
        const hasExistingFile = bacaan?.file_bacaan;

        // Jika dalam mode edit dan ada file lama
        if (isCurrentlyEditing && hasExistingFile) {
          // Jika tidak ada file baru diunggah, ini valid karena file lama akan dipertahankan
          if (value === null) {
            return true;
          }
        }

        // Jika ada file baru yang diunggah
        if (value instanceof File) {
          return true;
        }

        // Jika bukan mode edit (add baru) atau mode edit tapi tidak ada file lama, maka file baru wajib ada
        return !!value; // Cek apakah 'value' (file input baru) ada
      }),
  });


  // formik harus dideklarasikan setelah handleSubmit dan validationSchema
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id_tipe_konten: bacaan?.id_tipe_konten || "",
      id_jenis_soal: bacaan?.id_jenis_soal || "",
      id_jenis_seleksi: bacaan?.id_jenis_seleksi || "",
      // PASTIKAN NAMA PROPERTI INI KONSISTEN DENGAN API
      id_kategori_paket: bacaan?.id_kategori_paket ? String(bacaan.id_kategori_paket) : "", // Konversi ke string jika API mengembalikan angka
      judul: bacaan?.judul || "",
      deskripsi: bacaan?.deskripsi || "",
      thumbnail_image: null, // Always null for file input, actual image shown via URL
      file_bacaan: null, // Always null for file input, actual file shown via URL
      remove_thumbnail: false, // Flag to indicate if thumbnail should be removed
      isEdit: !!bacaan // Pass isEdit status to validation schema for conditional validation
    },
    validationSchema,
    onSubmit: handleSubmit
  });


  // Callback for filtering jenis seleksi in the table filters
  const filterJenisSeleksi = useCallback(async (idTipeKonten) => {
    try {
      if (!idTipeKonten) {
        dispatch({ type: 'FILTER_JENIS_SELEKSI', payload: [] });
        return;
      }

      const response = await fetchJenisSeleksi({
        id_tipe_konten: idTipeKonten,
        disable_pagination: true
      });

      const filtered = response.data || [];

      dispatch({ type: 'FILTER_JENIS_SELEKSI', payload: filtered });

      if (filters.id_jenis_seleksi && !filtered.some(s => s.id_jenis_seleksi === parseInt(filters.id_jenis_seleksi))) {
        dispatch({
          type: 'SET_FILTERS',
          payload: { id_jenis_seleksi: "" }
        });
      }
      // Re-evaluate jenis soal filter after jenis seleksi is filtered
      if (filters.id_jenis_soal) {
          const jenisSoalFilteredForFilter = jenisSoalOptions.filter(soal =>
            filters.id_jenis_seleksi === "" || soal.id_jenis_seleksi === parseInt(filters.id_jenis_seleksi)
          );
          if (!jenisSoalFilteredForFilter.some(s => s.id_jenis_soal === parseInt(filters.id_jenis_soal))) {
            dispatch({
              type: 'SET_FILTERS',
              payload: { id_jenis_soal: "" }
            });
          }
      }

    } catch (error) {
      console.error("Error filtering jenis seleksi (table):", error);
      toast.error(error.message || "Gagal memuat jenis seleksi (filter). Silakan coba lagi.");
    }
  }, [filters.id_jenis_seleksi, filters.id_jenis_soal, jenisSoalOptions, filters.id_tipe_konten]); // Added filters.id_tipe_konten for dependency

  // Callback for filtering jenis seleksi in the modal form
  const filterJenisSeleksiForModal = useCallback(async (idTipeKonten) => {
    try {
      if (!idTipeKonten) {
        dispatch({ type: 'FILTER_JENIS_SELEKSI_MODAL', payload: [] });
        return;
      }
      const response = await fetchJenisSeleksi({
        id_tipe_konten: idTipeKonten,
        disable_pagination: true
      });
      dispatch({ type: 'FILTER_JENIS_SELEKSI_MODAL', payload: response.data || [] });
    } catch (error) {
      console.error("Error filtering jenis seleksi (modal):", error);
      toast.error(error.message || "Gagal memuat jenis seleksi (modal). Silakan coba lagi.");
    }
  }, []);

  // Callback for fetching jenis soal options in the modal form
  const fetchJenisSoalForModal = useCallback(async (idTipeKonten, idJenisSeleksi) => {
    if (!idTipeKonten || !idJenisSeleksi) {
      dispatch({ type: 'SET_JENIS_SOAL_MODAL_OPTIONS', payload: [] });
      return;
    }

    dispatch({ type: 'SET_STATE', payload: { loadingJenisSoalModal: true } });
    try {
      const response = await fetchJenisSoal({
        id_tipe_konten: idTipeKonten,
        id_jenis_seleksi: idJenisSeleksi,
        disable_pagination: true
      });

      if (response.success && response.data) {
        dispatch({ type: 'SET_JENIS_SOAL_MODAL_OPTIONS', payload: response.data });
      } else {
        dispatch({ type: 'SET_JENIS_SOAL_MODAL_OPTIONS', payload: [] });
        console.error("Failed to fetch jenis soal for modal:", response.message);
      }
    } catch (error) {
      console.error("Error fetching jenis soal for modal:", error);
      dispatch({ type: 'SET_JENIS_SOAL_MODAL_OPTIONS', payload: [] });
    } finally {
      dispatch({ type: 'SET_STATE', payload: { loadingJenisSoalModal: false } });
    }
  }, []);

  // Fetch initial data (Bacaan, Options for filters and modal)
  const fetchData = useCallback(async () => {
    if (!isOptionsLoaded) return; // Wait until options are loaded

    try {
      dispatch({ type: 'SET_STATE', payload: { loading: true } });

      const response = await fetchBacaan(
        pagination.page,
        pagination.per_page,
        filters
      );

      if (response && response.success) {
        const calculatedLastPage = Math.ceil(response.meta.total / response.meta.per_page);
        const bacaanData = (response.data || []).map(bacaanItem => ({
          ...bacaanItem,
          // Ensure these fields exist, even if null/undefined from API
          jenis_seleksi_nama: bacaanItem.jenis_seleksi_nama || '-',
          jenis_soal_nama: bacaanItem.jenis_soal_nama || '-',
          tipe_konten_nama: bacaanItem.tipe_konten_nama || '-',
          kategori: bacaanItem.kategori || '-' // Gunakan kategori_nama dari API response
        }));

        dispatch({
          type: 'SET_STATE',
          payload: {
            data: bacaanData,
            pagination: {
              ...pagination,
              total: response.meta.total || 0,
              last_page: calculatedLastPage || 1
            },
            loading: false
          }
        });
      } else {
        throw new Error(response?.message || "Gagal memuat data bacaan.");
      }
    } catch (error) {
      console.error("Error in fetchData:", error);
      dispatch({
        type: 'SET_STATE',
        payload: {
          data: [],
          loading: false,
          pagination: {
            ...pagination,
            total: 0,
            last_page: 1
          }
        }
      });
      toast.error(error.message || "Terjadi kesalahan saat memuat data bacaan.");
    }
  }, [isOptionsLoaded, pagination.page, pagination.per_page, filters]);


// Di dalam komponen BacaanList

// Load options termasuk kategori
// Di dalam komponen BacaanList, di fungsi loadOptions:
const loadOptions = async () => {
  try {
    dispatch({ type: 'SET_STATE', payload: { loadingOptions: true } });

    const [jenisSoalRes, tipeKontenRes, kategoriRes] = await Promise.all([
      fetchJenisSoal({ disable_pagination: true }),
      // Panggil fetchTipeKonten dengan objek filter
      fetchTipeKonten({ disable_pagination: true }), // Ini akan menghasilkan URL tanpa `page` dan `per_page`
      fetchKategori({ disable_pagination: true })
    ]);

    const kategoriOptions = kategoriRes.success ? kategoriRes.data : [];

    dispatch({
      type: 'SET_STATE',
      payload: {
        jenisSoalOptions: jenisSoalRes.data || [],
        tipeKontenOptions: tipeKontenRes.data || [],
        kategoriOptions,
        isOptionsLoaded: true,
        loadingOptions: false
      }
    });
  } catch (error) {
    console.error("Error loading options:", error);
    toast.error("Gagal memuat opsi pilihan. Silakan coba lagi.");
    dispatch({ type: 'SET_STATE', payload: { loadingOptions: false } });
  }
};

  // Effects for data fetching and filtering
  useEffect(() => {
    loadOptions();
  }, []); // Run only once on component mount

  useEffect(() => {
    if (isOptionsLoaded) {
      fetchData();
    }
  }, [isOptionsLoaded, fetchData]);

  useEffect(() => {
    if (isOptionsLoaded && filters.id_tipe_konten) {
      filterJenisSeleksi(filters.id_tipe_konten);
    }
  }, [filters.id_tipe_konten, isOptionsLoaded, filterJenisSeleksi]);

  // Effect to handle single bacaan edit from URL
  useEffect(() => {
    if (idBacaan && isOptionsLoaded) {
      const fetchSingleBacaan = async (id) => {
        try {
          dispatch({ type: 'SET_STATE', payload: { loading: true } });
          const response = await fetchBacaan(1, 1, { id_bacaan: id });

          if (response.success && response.data.length > 0) {
            const fetchedBacaan = response.data[0];
            dispatch({
              type: 'SET_STATE',
              payload: {
                bacaan: fetchedBacaan,
                isEdit: true,
                modal: true,
                loading: false
              }
            });
            // Set formik values when opening modal for edit
            formik.setValues({
              id_tipe_konten: fetchedBacaan.id_tipe_konten || "",
              id_jenis_seleksi: fetchedBacaan.id_jenis_seleksi || "",
              id_jenis_soal: fetchedBacaan.id_jenis_soal || "",
              // Pastikan konversi ke string jika `id_kategori_paket` dari API adalah number
              id_kategori_paket: fetchedBacaan.id_kategori_paket ? String(fetchedBacaan.id_kategori_paket) : "",
              judul: fetchedBacaan.judul || "",
              deskripsi: fetchedBacaan.deskripsi || "",
              thumbnail_image: null, // File inputs must be null for new selection
              file_bacaan: null, // File inputs must be null for new selection
              remove_thumbnail: false, // Reset this flag when a new item is loaded for edit
              isEdit: true // Explicitly set isEdit for formik
            });
          } else {
            console.error("Bacaan not found or failed to fetch:", response.message);
            toast.error("Data bacaan tidak ditemukan.");
            dispatch({ type: 'SET_STATE', payload: { loading: false } });
            navigate(location.pathname, { replace: true }); // Remove id_bacaan from URL if not found
          }
        } catch (error) {
          console.error("Error fetching single bacaan:", error);
          toast.error(error.message || "Terjadi kesalahan saat memuat data bacaan.");
          dispatch({ type: 'SET_STATE', payload: { loading: false } });
          navigate(location.pathname, { replace: true });
        }
      };
      fetchSingleBacaan(idBacaan);
    }
  }, [idBacaan, isOptionsLoaded, navigate, location.pathname, formik, bacaan]);


  // Effects for modal-specific filtering
  useEffect(() => {
    if (modal && formik.values.id_tipe_konten) {
      filterJenisSeleksiForModal(formik.values.id_tipe_konten);
    } else if (modal && !formik.values.id_tipe_konten) {
      dispatch({ type: 'FILTER_JENIS_SELEKSI_MODAL', payload: [] });
    }
  }, [modal, formik.values.id_tipe_konten, filterJenisSeleksiForModal]);

  useEffect(() => {
    if (modal && formik.values.id_tipe_konten && formik.values.id_jenis_seleksi) {
      fetchJenisSoalForModal(formik.values.id_tipe_konten, formik.values.id_jenis_seleksi);
    } else if (modal) {
      dispatch({ type: 'SET_JENIS_SOAL_MODAL_OPTIONS', payload: [] });
    }
  }, [modal, formik.values.id_tipe_konten, formik.values.id_jenis_seleksi, fetchJenisSoalForModal]);


  const handlePageChange = useCallback((newPage) => {
    if (newPage < 1 || newPage > pagination.last_page) return;

    dispatch({
      type: 'SET_PAGINATION',
      payload: { page: newPage }
    });
  }, [pagination.last_page]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'id_tipe_konten') {
      dispatch({
        type: 'SET_FILTERS',
        payload: {
          [name]: value,
          id_jenis_seleksi: "",
          id_jenis_soal: ""
        }
      });
    } else if (name === 'id_jenis_seleksi') {
      dispatch({
        type: 'SET_FILTERS',
        payload: {
          [name]: value,
          id_jenis_soal: ""
        }
      });
    } else {
      dispatch({
        type: 'SET_FILTERS',
        payload: { [name]: value }
      });
    }
  };

  const clearFilters = () => {
    dispatch({ type: 'RESET_FILTERS' });
  };

const toggleModal = useCallback(() => {
  if (modal) {
    // Saat menutup modal
    dispatch({
      type: 'SET_STATE',
      payload: {
        modal: false,
        bacaan: null,
        isEdit: false,
        jenisSoalModalOptions: [],
        filteredJenisSeleksiModal: [],
      }
    });
    formik.resetForm();
  } else {
    // Saat membuka modal
    dispatch({
      type: 'SET_STATE',
      payload: {
        modal: true,
        isEdit: false,
        bacaan: null,
        deleteModal: false // Pastikan delete modal tertutup
      }
    });
    formik.resetForm();
  }
}, [modal, formik]);

const handleBacaanClick = useCallback((bacaanData, e) => {
  // Pastikan event dihentikan dengan benar
  if (e) {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
  }

  dispatch({
    type: 'SET_STATE',
    payload: {
      bacaan: bacaanData,
      isEdit: true,
      modal: true,
      deleteModal: false // Pastikan modal delete tertutup
    }
  });
}, []);

  const handleCloseDeleteModal = useCallback(() => {
    dispatch({ type: 'SET_STATE', payload: { deleteModal: false, bacaan: null } });
  }, []);

  const handleConfirmDelete = async () => {
    try {
      if (!bacaan?.id_bacaan) return;

      const response = await deleteBacaan(bacaan.id_bacaan);
      if (response.success) {
        toast.success("Bacaan berhasil dihapus.");
        const newPage = data.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page;
        dispatch({
          type: 'SET_PAGINATION',
          payload: { page: newPage }
        });
        handleCloseDeleteModal();
        fetchData(); // Re-fetch data after deletion
      } else {
        throw new Error(response.message || "Gagal menghapus bacaan.");
      }
    } catch (error) {
      console.error("Error deleting bacaan:", error);
      toast.error(error.message || "Gagal menghapus bacaan.");
    }
  };

const onClickDelete = useCallback((bacaanData, e) => {
  e?.preventDefault();
  e?.stopPropagation();

  dispatch({
    type: 'SET_STATE',
    payload: {
      bacaan: bacaanData,
      deleteModal: true,
      modal: false // Tutup modal edit jika terbuka
    }
  });
}, []);

  const renderImagePreview = useCallback(() => {
    const fileInput = formik.values.thumbnail_image;
    const existingImageUrl = bacaan?.thumbnail_image;

    // If a new file is selected
    if (fileInput instanceof File) {
      return (
        <div className="mt-2">
          <img
            src={URL.createObjectURL(fileInput)}
            alt="Preview thumbnail baru"
            className="img-thumbnail"
            style={{ maxWidth: '100px', maxHeight: '100px' }}
          />
          <small className="d-block text-muted">Preview Thumbnail Baru</small>
          <Button
            size="sm"
            color="light"
            className="mt-1"
            onClick={() => {
              formik.setFieldValue("thumbnail_image", null);
              formik.setFieldValue("remove_thumbnail", false); // If user uploads then deletes, it's not "removing existing"
            }}
          >
            Hapus
          </Button>
        </div>
      );
    }
    // If in edit mode and there's an existing image, and it's not marked for removal
    else if (isEdit && existingImageUrl && !formik.values.remove_thumbnail) {
      return (
        <div className="mt-2">
          <img
            src={existingImageUrl}
            alt="Thumbnail saat ini"
            className="img-thumbnail"
            style={{ maxWidth: '100px', maxHeight: '100px' }}
          />
          <small className="d-block text-muted">Thumbnail Saat Ini</small>
          <Button
            size="sm"
            color="light"
            className="mt-1"
            onClick={() => {
              formik.setFieldValue("thumbnail_image", null); // Clear input
              formik.setFieldValue("remove_thumbnail", true); // Mark for removal on submit
            }}
          >
            Hapus Thumbnail Ini
          </Button>
        </div>
      );
    }
    // If it's a new entry, or existing thumbnail is marked for removal
    else {
      return (
        <div className="mt-2 text-muted">
          <small>{!isEdit ? 'Thumbnail wajib diisi untuk bacaan baru' : 'Thumbnail akan dihapus saat disimpan atau belum ada.'}</small>
        </div>
      );
    }
  }, [formik.values.thumbnail_image, isEdit, bacaan, formik.values.remove_thumbnail, formik]);


  const renderFilePreview = useCallback(() => {
    const fileInput = formik.values.file_bacaan;
    const existingFileUrl = bacaan?.file_bacaan;

    if (fileInput instanceof File) {
      return (
        <div className="mt-2">
          <div className="d-flex align-items-center">
            <i className="mdi mdi-file-pdf font-size-24 text-danger me-2" />
            <span>{fileInput.name}</span>
          </div>
          <small className="d-block text-muted">File Baru</small>
          <Button
            size="sm"
            color="light"
            className="mt-1"
            onClick={() => formik.setFieldValue("file_bacaan", null)}
          >
            Hapus
          </Button>
        </div>
      );
    } else if (isEdit && existingFileUrl) {
      return (
        <div className="mt-2">
          <div className="d-flex align-items-center">
            <i className="mdi mdi-file-pdf font-size-24 text-danger me-2" />
            <span>File yang sudah diupload</span>
          </div>          
         <small className="d-block text-muted">
          <button onClick={() => handleViewFile(existingFileUrl)} className="btn btn-link p-0 m-0">
            Lihat file
          </button>
        </small>

          <Button
            size="sm"
            color="light"
            className="mt-1"
            onClick={() => formik.setFieldValue("file_bacaan", null)} // Allow removal of existing file
          >
            Hapus File Ini
          </Button>
        </div>
      );
    } else {
      return (
        <div className="mt-2 text-muted">
          <small>File bacaan wajib diisi (format PDF)</small>
        </div>
      );
    }
  }, [formik.values.file_bacaan, isEdit, bacaan, formik]);

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      ['link'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link'
  ];

  const columns = useMemo(() => [
    {
      header: 'No',
      accessorKey: "id_bacaan",
      enableColumnFilter: false,
      cell: (cell) => (
        <span className="text-muted">
          {((pagination.page - 1) * pagination.per_page) + cell.row.index + 1}
        </span>
      )
    },
    {
      header: 'Tipe Konten',
      accessorKey: "tipe_konten", // Pastikan ini sesuai dengan key di data dari API
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge color="primary" className="font-size-12">
          {cell.getValue() || '-'}
        </Badge>
      )
    },
    {
      header: 'Jenis Seleksi',
      accessorKey: "jenis_seleksi_nama",
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge color="info" className="font-size-12">
          {cell.getValue() || '-'}
        </Badge>
      )
    },
    {
      header: 'Jenis Soal',
      accessorKey: "jenis_soal", // Pastikan ini sesuai dengan key di data dari API
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge color="success" className="font-size-12">
          {cell.getValue() || '-'}
        </Badge>
      )
    },
    {
      header: 'Kategori',
      accessorKey: "kategori", // Ini akan menggunakan properti 'kategori' yang sudah di-map di fetchData
      enableColumnFilter: false,
      cell: (cell) => (
        <Badge color="warning" className="font-size-12">
          {cell.getValue() || '-'}
        </Badge>
      )
    },
    {
      header: 'Judul Bacaan',
      accessorKey: "judul",
      enableColumnFilter: false,
      cell: (cell) => (
        <div className="text-truncate" style={{ maxWidth: '200px' }}>
          {cell.getValue() || '-'}
        </div>
      )
    },
    {
      header: 'Dibuat',
      accessorKey: "created_at",
      enableColumnFilter: false,
      cell: (cellProps) => {
          const date = new Date(cellProps.getValue());
          const year = date.getFullYear();
          const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
          const day = date.getDate().toString().padStart(2, '0');
          return `${year}-${month}-${day}`; // Format tanggal menjadi YYYY-MM-DD
      }
    },
{
  header: 'Aksi',
  accessorKey: "actions",
  enableColumnFilter: false,
  enableSorting: false,
  cell: (cellProps) => {
    const original = cellProps.row.original;
    return (
      <div className="hstack gap-2">
        <Button
          color="info"
          size="sm"
          className="btn-icon"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleBacaanClick(original, e);
          }}
          id={`edit-${original.id_bacaan}`}
        >
          <i className="mdi mdi-pencil font-size-12" />
        </Button>
        <UncontrolledTooltip placement="top" target={`edit-${original.id_bacaan}`}>
            Edit
        </UncontrolledTooltip>

        <Button
          color="danger"
          size="sm"
          className="btn-icon"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClickDelete(original, e);
          }}
          id={`delete-${original.id_bacaan}`}
        >
          <i className="mdi mdi-delete font-size-12" />
        </Button>
        <UncontrolledTooltip placement="top" target={`delete-${original.id_bacaan}`}>
            Hapus
        </UncontrolledTooltip>
      </div>
    );
  }
}
  ], [pagination.page, pagination.per_page, handleBacaanClick, onClickDelete]);

  return (
    <React.Fragment>
      <ToastContainer position="top-right" autoClose={3000} />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleConfirmDelete}
        onCloseClick={handleCloseDeleteModal}
      />

      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Bacaan" breadcrumbItem="Daftar Bacaan" />

          <Row>
            <Col xl={12}>
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center justify-content-between mb-5">
                    <h4 className="card-title">Daftar Bacaan</h4>
                    <Button color="primary" className="btn" onClick={toggleModal}>
                      <i className="mdi mdi-plus me-1" /> Tambah Bacaan
                    </Button>
                  </div>

                  {/* Filter Section */}
                  <Row className="mb-3">
                    <Col md={3}>
                      <div className="mb-3">
                        <Label htmlFor="filter_tipe_konten">Tipe Konten</Label>
                        <Input
                          type="select"
                          name="id_tipe_konten"
                          id="filter_tipe_konten"
                          value={filters.id_tipe_konten}
                          onChange={handleFilterChange}
                        >
                          <option value="">Semua Tipe Konten</option>
                          {tipeKontenOptions.map((option) => (
                            <option key={option.id_tipe_konten} value={option.id_tipe_konten}>
                              {option.tipe_konten}
                            </option>
                          ))}
                        </Input>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label htmlFor="filter_jenis_seleksi">Jenis Seleksi</Label>
                        <Input
                          type="select"
                          name="id_jenis_seleksi"
                          id="filter_jenis_seleksi"
                          value={filters.id_jenis_seleksi}
                          onChange={handleFilterChange}
                          disabled={!filters.id_tipe_konten}
                        >
                          <option value="">Semua Jenis Seleksi</option>
                          {filteredJenisSeleksi.map((option) => (
                            <option key={option.id_jenis_seleksi} value={option.id_jenis_seleksi}>
                              {option.nama_seleksi}
                            </option>
                          ))}
                        </Input>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="mb-3">
                        <Label htmlFor="filter_jenis_soal">Jenis Soal</Label>
                        <Input
                          type="select"
                          name="id_jenis_soal"
                          id="filter_jenis_soal"
                          value={filters.id_jenis_soal}
                          onChange={handleFilterChange}
                          disabled={!filters.id_jenis_seleksi}
                        >
                          <option value="">Semua Jenis Soal</option>
                          {jenisSoalOptions
                            .filter(soal => filters.id_tipe_konten === "" || soal.id_tipe_konten === parseInt(filters.id_tipe_konten))
                            .filter(soal => filters.id_jenis_seleksi === "" || soal.id_jenis_seleksi === parseInt(filters.id_jenis_seleksi))
                            .map((option) => (
                              <option key={option.id_jenis_soal} value={option.id_jenis_soal}>
                                {option.nama_jenis}
                              </option>
                            ))}
                        </Input>
                      </div>
                    </Col>
                     <Col md={3}>
                      <div className="mb-3">
                        <Label htmlFor="filter_kategori">Kategori</Label>
                        <Input
                          type="select"
                          name="id_kategori_paket" // Pastikan name sesuai dengan state filter
                          id="filter_kategori"
                          value={filters.id_kategori_paket}
                          onChange={handleFilterChange}
                        >
                          <option value="">Semua Kategori</option>
                          {kategoriOptions.map((option) => (
                            <option key={option.id_kategori_paket} value={option.id_kategori_paket}>
                              {option.kategori}
                            </option>
                          ))}
                        </Input>
                      </div>
                    </Col>


                      <Col md={2} className="d-flex align-items-end">
                                              <Button
                                                color="danger"
                                                outline
                                                onClick={clearFilters}
                                                className="w-100"
                                              >
                                                <i className="mdi mdi-refresh me-1"></i> Reset
                                              </Button>
                                            </Col>
                  </Row>

                  {loading ? (
                    <Spinners setLoading={loading} />
                  ) : (
                    <>
                      <TableContainer
                        columns={columns}
                        data={data}
                        isGlobalFilter={false} // Filter sudah dihandle di atas
                        isAddOptions={false}
                        customPageSize={pagination.per_page}
                        tableClass="align-middle table-nowrap dt-responsive nowrap w-100 dataTable no-footer"
                        theadClass="table-light"
                        pagination={false} // Handle pagination secara manual
                      />

                      {/* Bagian Paginasi */}
                      <div className="d-flex justify-content-end mt-3">
                        <Pagination className="pagination-rounded mb-0">
                          <PaginationItem disabled={pagination.page <= 1}>
                            <PaginationLink
                              previous
                              href="#"
                              onClick={() => handlePageChange(pagination.page - 1)}
                            />
                          </PaginationItem>
                          {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map((page) => (
                            <PaginationItem key={page} active={page === pagination.page}>
                              <PaginationLink
                                href="#"
                                onClick={() => handlePageChange(page)}
                              >
                                {page}
                              </PaginationLink>
                            </PaginationItem>
                          ))}
                          <PaginationItem disabled={pagination.page >= pagination.last_page}>
                            <PaginationLink
                              next
                              href="#"
                              onClick={() => handlePageChange(pagination.page + 1)}
                            />
                          </PaginationItem>
                        </Pagination>
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={toggleModal} centered size="lg">
        <ModalHeader toggle={toggleModal}>
          {isEdit ? "Edit Bacaan" : "Tambah Bacaan"}
        </ModalHeader>
        <Form onSubmit={formik.handleSubmit}>
          <ModalBody>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="id_tipe_konten" className="form-label">
                    Tipe Konten <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="id_tipe_konten"
                    type="select"
                    id="id_tipe_konten"
                    className="form-select"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.id_tipe_konten}
                    invalid={
                      formik.touched.id_tipe_konten && formik.errors.id_tipe_konten ? true : false
                    }
                  >
                    <option value="">Pilih Tipe Konten</option>
                    {tipeKontenOptions.map((option) => (
                      <option key={option.id_tipe_konten} value={option.id_tipe_konten}>
                        {option.tipe_konten}
                      </option>
                    ))}
                  </Input>
                  {formik.touched.id_tipe_konten && formik.errors.id_tipe_konten ? (
                    <FormFeedback type="invalid">
                      {formik.errors.id_tipe_konten}
                    </FormFeedback>
                  ) : null}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="id_jenis_seleksi" className="form-label">
                    Jenis Seleksi <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="id_jenis_seleksi"
                    type="select"
                    id="id_jenis_seleksi"
                    className="form-select"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.id_jenis_seleksi}
                    disabled={!formik.values.id_tipe_konten || loadingOptions}
                    invalid={
                      formik.touched.id_jenis_seleksi && formik.errors.id_jenis_seleksi ? true : false
                    }
                  >
                    <option value="">Pilih Jenis Seleksi</option>
                    {filteredJenisSeleksiModal.map((option) => (
                      <option key={option.id_jenis_seleksi} value={option.id_jenis_seleksi}>
                        {option.nama_seleksi}
                      </option>
                    ))}
                  </Input>
                  {formik.touched.id_jenis_seleksi && formik.errors.id_jenis_seleksi ? (
                    <FormFeedback type="invalid">
                      {formik.errors.id_jenis_seleksi}
                    </FormFeedback>
                  ) : null}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="id_jenis_soal" className="form-label">
                    Jenis Soal <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="id_jenis_soal"
                    type="select"
                    id="id_jenis_soal"
                    className="form-select"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.id_jenis_soal}
                    disabled={!formik.values.id_jenis_seleksi || loadingJenisSoalModal}
                    invalid={
                      formik.touched.id_jenis_soal && formik.errors.id_jenis_soal ? true : false
                    }
                  >
                    <option value="">Pilih Jenis Soal</option>
                    {jenisSoalModalOptions.map((option) => (
                      <option key={option.id_jenis_soal} value={option.id_jenis_soal}>
                        {option.nama_jenis}
                      </option>
                    ))}
                  </Input>
                  {formik.touched.id_jenis_soal && formik.errors.id_jenis_soal ? (
                    <FormFeedback type="invalid">
                      {formik.errors.id_jenis_soal}
                    </FormFeedback>
                  ) : null}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="id_kategori_paket" className="form-label">
                    Kategori <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="id_kategori_paket"
                    type="select"
                    id="id_kategori_paket"
                    className="form-select"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.id_kategori_paket}
                    invalid={
                      formik.touched.id_kategori_paket && formik.errors.id_kategori_paket ? true : false
                    }
                  >
                    <option value="">Pilih Kategori</option>
                    {kategoriOptions.map((option) => (
                      <option key={option.id_kategori_paket} value={option.id_kategori_paket}>
                        {option.kategori}
                      </option>
                    ))}
                  </Input>
                  {formik.touched.id_kategori_paket && formik.errors.id_kategori_paket ? (
                    <FormFeedback type="invalid">
                      {formik.errors.id_kategori_paket}
                    </FormFeedback>
                  ) : null}
                </div>
              </Col>
              <Col md={12}>
                <div className="mb-3">
                  <Label htmlFor="judul" className="form-label">
                    Judul Bacaan <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="judul"
                    type="text"
                    id="judul"
                    placeholder="Masukkan judul bacaan"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.judul}
                    invalid={
                      formik.touched.judul && formik.errors.judul ? true : false
                    }
                  />
                  {formik.touched.judul && formik.errors.judul ? (
                    <FormFeedback type="invalid">
                      {formik.errors.judul}
                    </FormFeedback>
                  ) : null}
                </div>
              </Col>
              <Col md={12}>
                <div className="mb-3">
                  <Label htmlFor="deskripsi" className="form-label">
                    Deskripsi <span className="text-danger">*</span>
                  </Label>
                  <ReactQuill
                    theme="snow"
                    value={formik.values.deskripsi}
                    onChange={(content) => formik.setFieldValue("deskripsi", content === '<p><br></p>' ? '' : content)}
                    onBlur={(range, source, quill) => formik.setFieldTouched("deskripsi", true)}
                    modules={modules}
                    formats={formats}
                    className={
                      formik.touched.deskripsi && formik.errors.deskripsi ? 'is-invalid' : ''
                    }
                  />
                  {formik.touched.deskripsi && formik.errors.deskripsi ? (
                    <div className="invalid-feedback d-block">
                      {formik.errors.deskripsi}
                    </div>
                  ) : null}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="thumbnail_image" className="form-label">
                    Thumbnail (JPG, JPEG, PNG, maks 2MB)
                  </Label>
                  <Input
                    name="thumbnail_image"
                    type="file"
                    id="thumbnail_image"
                    className="form-control"
                    accept=".jpg,.jpeg,.png"
                    onChange={(event) => {
                      formik.setFieldValue("thumbnail_image", event.currentTarget.files[0]);
                      formik.setFieldValue("remove_thumbnail", false); // Unset remove flag if new file selected
                    }}
                    onBlur={formik.handleBlur}
                    invalid={
                      formik.touched.thumbnail_image && formik.errors.thumbnail_image ? true : false
                    }
                  />
                  {formik.touched.thumbnail_image && formik.errors.thumbnail_image ? (
                    <FormFeedback type="invalid">
                      {formik.errors.thumbnail_image}
                    </FormFeedback>
                  ) : null}
                  {renderImagePreview()}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="file_bacaan" className="form-label">
                    File Bacaan (PDF, maks 5MB)
                  </Label>
                  <Input
                    name="file_bacaan"
                    type="file"
                    id="file_bacaan"
                    className="form-control"
                    accept=".pdf"
                    onChange={(event) => {
                      formik.setFieldValue("file_bacaan", event.currentTarget.files[0]);
                    }}
                    onBlur={formik.handleBlur}
                    invalid={
                      formik.touched.file_bacaan && formik.errors.file_bacaan ? true : false
                    }
                  />
                  {formik.touched.file_bacaan && formik.errors.file_bacaan ? (
                    <FormFeedback type="invalid">
                      {formik.errors.file_bacaan}
                    </FormFeedback>
                  ) : null}
                  {renderFilePreview()}
                </div>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="light" onClick={toggleModal}>
              Tutup
            </Button>
            <Button type="submit" color="primary" disabled={formik.isSubmitting}>
              {formik.isSubmitting ? "Menyimpan..." : (isEdit ? "Simpan Perubahan" : "Simpan")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default BacaanList;